import { snake } from 'change-case'
import { HumanErrorMessages, HumanInfoMessages, MaxCheckableNumber } from '../../constants'
import { api } from '../../libs/api'
import { MySet as Set } from '../../libs/polyfill'
import pagination from './pagination'

export default {
  namespaced: true,

  modules: {
    pagination: pagination(),
  },

  state: {
    comment: '',
    index: [],
    pageIds: [],
    checkedIds: new Set([]),
    pageCheckedIds: new Set([]),
    detail: {},
    errors: [],
    // 直近の fetchIndex のエラー（成功時は null）。avoidBlank() で使う。
    lastFetchError: null,
  },
  getters: {
    checkedCount: state => state.checkedIds.size,
    checkedIds: state => state.checkedIds,
    checked: (_, getters) => getters.checkedCount > 0,
    markedIds: state => new Set(state.index.map(({ id }) => id)),
    marked: (state, getters) => id => getters.markIds.has(id),
    // eslint-disable-next-line
    pageAllChecked: (state, getters) => getters.checkedCount
      ? !state.pageIds.find(n => !state.pageCheckedIds.has(n))
      : false,
    sending: state => state.showAddAccountModal,
    opportunityGroups: state => state.opportunityGroups,
  },
  mutations: {
    comment: (state, value) => state.comment = value,
    updater: (state, value) => state.detail.comment_updater = value.updater,
    errors: (state, errors) => state.errors = errors,
    index: (state, index) => {
      const pageIds = index.map(({ id }) => id)
      const pageCheckedIds = pageIds.filter(id => state.checkedIds.has(id))
      const newIndex = index.map((company) => {
        const newCompany = Object.assign({}, company)
        newCompany.marked = company.in_job_mark
        newCompany.job_mark_user = company.job_mark_user
        return newCompany
      })
      state.index = newIndex
      state.pageIds = pageIds
      state.pageCheckedIds = new Set(pageCheckedIds)
    },
    check: (state, id) => {
      const newCheckedIds = new Set(state.checkedIds)
      const newPageCheckedIds = new Set(state.pageCheckedIds)
      if (Array.isArray(id)) {
        id.forEach((i) => {
          newCheckedIds.add(i)
          newPageCheckedIds.add(i)
        })
      } else {
        newCheckedIds.add(id)
        newPageCheckedIds.add(id)
      }
      state.checkedIds = newCheckedIds
      state.pageCheckedIds = newPageCheckedIds
    },
    mark: (state, { ids, newMarked, markUser }) => {
      let newIndex = state.index
      let companyIds = ids
      if (!Array.isArray(companyIds)) {
        companyIds = [companyIds]
      }
      companyIds.forEach((companyId) => {
        newIndex = newIndex.map((company) => {
          if (company.company_id === companyId) {
            const newCompany = Object.assign({}, company)
            newCompany.marked = newMarked
            newMarked
            ? newCompany.job_mark_user = markUser
            : newCompany.job_mark_user = ''
            return newCompany
          }
          return company
        })
      })
      state.index = newIndex
    },
    uncheck: (state, id) => {
      const newCheckedIds = new Set(state.checkedIds)
      const newPageCheckedIds = new Set(state.pageCheckedIds)
      if (Array.isArray(id)) {
        id.forEach((i) => {
          newCheckedIds.delete(i)
          newPageCheckedIds.delete(i)
        })
      } else {
        newCheckedIds.delete(id)
        newPageCheckedIds.delete(id)
      }

      state.checkedIds = newCheckedIds
      state.pageCheckedIds = newPageCheckedIds
    },
    uncheckAll: (state) => {
      state.checkedIds.clear()
      state.pageCheckedIds.clear()

      state.checkedIds = new Set(state.checkedIds)
      state.pageCheckedIds = new Set(state.pageCheckedIds)
    },
    clearDetail: state => state.detail = {},
    detail: (state, detail) => state.detail = detail,
    copyCheckedIds: (state, ids) => {
      state.checkedIds = new Set(ids)
    },
    updateIndexForAccount: (state, checkedIds) => {
      const newIndex = state.index.map((company) => {
        if (checkedIds.includes(company.company_id.toString())) {
          const newCompany = Object.assign({}, company)
          newCompany.is_account = true
          return newCompany
        }
        return company
      })
      state.index = newIndex
    },
    hide: (state, id) => {
      let newIndex = state.index
      newIndex = newIndex.map((company) => {
        if (company.id === id) {
          const newCompany = Object.assign({}, company)
          newCompany.hidden = true
          return newCompany
        }
        return company
      })
      state.index = newIndex
    },
    lastFetchError: (state, error) => state.lastFetchError = error,
  },
  actions: {
    async comment ({ commit }, { companyId, comment }) {
      try {
        await api.updateCompanyComment({
          companyId,
          comment,
        })
      } catch (e) {
        commit('errors', e)
        return Promise.reject(e)
      }
    },
    toggleCheck ({ commit, dispatch, state: { checkedIds } }, { checked, id }) {
      if (checked && checkedIds.size >= MaxCheckableNumber) {
        dispatch(
          'common/pushToastError',
          { error: HumanErrorMessages.CHECKED_LIMIT },
          { root: true },
        )
        return
      }

      checked
        ? commit('check', id)
        : commit('uncheck', id)
    },
    togglePageCheck (
      { commit, dispatch, getters: { pageAllChecked }, state: { pageIds, checkedIds } },
      { event }) {
      if (!pageAllChecked) {
        const nextCheckedIds = new Set(checkedIds)
        pageIds.forEach((i) => { nextCheckedIds.add(i) })
        if (nextCheckedIds.size > MaxCheckableNumber) {
          dispatch(
            'common/pushToastError',
            { error: HumanErrorMessages.CHECKED_LIMIT },
            { root: true },
          )
          event.preventDefault()
          return
        }
      }
      pageAllChecked
        ? commit('uncheck', pageIds)
        : commit('check', pageIds)
    },
    downloadCompaniesCsv ({ state: { checkedIds } }, { allColumns }) {
      if (checkedIds.size === 0) {
        return
      }
      return api.downloadCompaniesCsv(checkedIds, allColumns)
    },
    async fetchIndex ({ commit, dispatch }, query) {
      try {
        const requestParameters = Object.keys(query).reduce((acc, key) => {
          acc[snake(key)] = query[key]
          return acc
        }, {})
        const { meta, index } = await api.fetchCompanies(requestParameters)
        commit('pagination/newState', meta)
        commit('index', index)
        commit('lastFetchError', null)
      } catch (e) {
        commit('lastFetchError', e)
        const message = (Array.isArray(e) && e[0] && e[0].message) || HumanErrorMessages.SEARCH_FAILED
        dispatch(
          'common/pushToastError',
          { error: { message } },
          { root: true },
        )
        return Promise.reject(e)
      }
    },
    async fetchDetail ({ commit }, { id }) {
      try {
        const response = await api.fetchCompany(id)
        commit('detail', response[0])
        commit('comment', response[0].comment)
      } catch (e) {
        return Promise.reject(e)
      }
    },
    async add ({ commit }, { id, ids }) {
      commit('uncheck', ids || id)
    },
    async remove ({ commit }, { id, ids }) {
      commit('uncheck', ids || id)
    },
    async removeCheckedCompanies ({ commit }) {
      commit('uncheckAll')
    },
    async hideCompany ({ commit, dispatch }, { id, companyName }) {
      try {
        await api.addExclusionCompanyName({ companyName })
        commit('hide', id)
        dispatch(
          'common/pushToastInfo',
          { info: HumanInfoMessages.ADD_EXCLUSION_COMPANY_NAME },
          { root: true },
        )
      } catch (e) {
        return Promise.reject(e)
      }
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/company.js